import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import { LoadingButton } from '../../common/LoadingButton';
import type { DRBFMCaseInputData } from '../../../types/drbfm';
import { translateWithFallback } from '../../../utils/translation';
import {
  caseIntakeSchema,
  type CaseIntakeFormValues,
} from '@/features/drbfm/schemas/caseIntake.schema';

interface DRBFMCaseIntakeStepProps {
  isLoading: boolean;
  onSubmit: (input: DRBFMCaseInputData) => void | Promise<unknown>;
}

type RequiredField = 'title' | 'productName' | 'targetPart' | 'changeSummary';

const DEFAULT_INPUT: DRBFMCaseInputData = {
  title: 'GDS front case FPC escape review',
  productName: 'display unit',
  targetPart: 'front case',
  changeSummary: 'FPC/cable escape enlarged during manufacturing transfer',
  sourceDocumentIds: [],
  context: { drawing_page_number: 2, drawing_image_id: 'img-page-2' },
};

const readSeedParams = (): Pick<DRBFMCaseInputData, 'sourceDocumentIds' | 'context'> => {
  if (typeof window === 'undefined') {
    return {
      sourceDocumentIds: DEFAULT_INPUT.sourceDocumentIds,
      context: DEFAULT_INPUT.context,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const documentId = params.get('documentId')?.trim();
  const imageId = params.get('imageId')?.trim() || DEFAULT_INPUT.context?.drawing_image_id;
  const pageNumberParam = Number(params.get('pageNumber'));
  const pageNumber = Number.isInteger(pageNumberParam) && pageNumberParam > 0
    ? pageNumberParam
    : DEFAULT_INPUT.context?.drawing_page_number;

  return {
    sourceDocumentIds: documentId ? [documentId] : DEFAULT_INPUT.sourceDocumentIds,
    context: {
      ...DEFAULT_INPUT.context,
      drawing_page_number: pageNumber,
      drawing_image_id: imageId,
    },
  };
};

const buildInitialInput = (): DRBFMCaseInputData => ({
  ...DEFAULT_INPUT,
  ...readSeedParams(),
});

const shortId = (value?: string) => {
  if (!value) return '';
  return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
};

const DRBFMCaseIntakeStep: React.FC<DRBFMCaseIntakeStepProps> = ({
  isLoading,
  onSubmit,
}) => {
  const { t } = useTranslation();
  // Seed (URL doc/page/image context) is not form state; it rides along on submit unchanged.
  const [seed] = useState(buildInitialInput);

  const form = useForm<CaseIntakeFormValues>({
    resolver: zodResolver(caseIntakeSchema),
    defaultValues: {
      title: seed.title,
      productName: seed.productName,
      targetPart: seed.targetPart,
      changeSummary: seed.changeSummary,
    },
    // RHF defaults, pinned deliberately — shipped error-clearing behaviour depends on them.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const values = form.watch();
  const input: DRBFMCaseInputData = { ...seed, ...values };
  const errors = form.formState.errors;
  const drawingPageNumberValue = input.context?.drawing_page_number;
  const drawingPageNumber = typeof drawingPageNumberValue === 'number' || typeof drawingPageNumberValue === 'string'
    ? drawingPageNumberValue
    : 2;
  const drawingImageId = String(input.context?.drawing_image_id || 'img-page-2');
  const sourceDocumentId = input.sourceDocumentIds?.[0];
  const fieldLabels: Record<RequiredField, string> = {
    title: t('drbfm.case.fields.title', 'ケースタイトル'),
    productName: t('drbfm.case.fields.productName', '製品'),
    targetPart: t('drbfm.case.fields.targetPart', '対象部品'),
    changeSummary: t('drbfm.case.fields.changeSummary', '変更概要'),
  };
  // `parsed` is already trimmed by the schema.
  const submit = form.handleSubmit(async (parsed) => {
    await onSubmit({
      ...seed,
      ...parsed,
      sourceDocumentIds: seed.sourceDocumentIds || [],
      context: seed.context || {},
    });
  });

  // noValidate: without it the browser's English tooltip blocks submit before
  // handleSubmit runs (jsdom never enforces this). `required` stays for a11y.
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <div>
        <h2 className="revy-cjk mb-2 text-xl font-medium leading-[1.5]">
          {t('drbfm.case.title', 'GDSケース作成')}
        </h2>
        <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
          {t('drbfm.case.description', '図面ページとGDS移管時の変更概要を起点にDRBFMケースを作成します。')}
        </p>
      </div>

      <Alert variant="info">
        <Info strokeWidth={1.9} />
        <AlertDescription className="revy-cjk revy-figure text-sm leading-[1.6]">
          {translateWithFallback(t, 'drbfm.case.referenceDrawing', `参照図面: ${drawingPageNumber}ページ目`, {
            page: drawingPageNumber,
          })}
          {sourceDocumentId ? ` / ${translateWithFallback(t, 'drbfm.case.documentId', `文書ID ${shortId(sourceDocumentId)}`, {
            id: shortId(sourceDocumentId),
          })}` : ''}
          {' / '}
          {translateWithFallback(t, 'drbfm.case.imageId', `画像ID ${shortId(drawingImageId)}`, {
            id: shortId(drawingImageId),
          })}
        </AlertDescription>
      </Alert>

      <div className="flex flex-col gap-4 min-[900px]:flex-row">
        <Field
          label={fieldLabels.title}
          error={errors.title?.message}
          required
          className="flex-1"
        >
          {(control) => (
            <Input
              {...control}
              {...form.register('title')}
              disabled={isLoading}
              required
            />
          )}
        </Field>
        <Field
          label={fieldLabels.productName}
          error={errors.productName?.message}
          required
          className="flex-1"
        >
          {(control) => (
            <Input
              {...control}
              {...form.register('productName')}
              disabled={isLoading}
              required
            />
          )}
        </Field>
      </div>

      <Field label={fieldLabels.targetPart} error={errors.targetPart?.message} required>
        {(control) => (
          <Input
            {...control}
            {...form.register('targetPart')}
            disabled={isLoading}
            required
          />
        )}
      </Field>

      <Field label={fieldLabels.changeSummary} error={errors.changeSummary?.message} required>
        {(control) => (
          <AutoGrowTextarea
            {...control}
            rows={4}
            {...form.register('changeSummary')}
            disabled={isLoading}
            required
          />
        )}
      </Field>

      <div className="flex justify-end">
        <LoadingButton
          type="submit"
          loading={isLoading}
          loadingText={t('drbfm.case.extractingChanges', '変更点を抽出中')}
        >
          <Search strokeWidth={1.9} />
          <span className="revy-cjk">{t('drbfm.case.extractChanges', '変更点を抽出')}</span>
        </LoadingButton>
      </div>
    </form>
  );
};

export default DRBFMCaseIntakeStep;
